import { useEffect, useState } from "react"

/**
 * Tracks whether a scroll container has content clipped beyond its top or
 * bottom edge. Attach the same `ref` to the scrollable element.
 *
 * `hasTopOverflow` is true while the element is scrollable and not scrolled to
 * its top edge; `hasBottomOverflow` is true while it's scrollable and not
 * scrolled to its bottom edge. Both are false when the element can't scroll.
 *
 * The state re-evaluates on scroll, when the element resizes (which affects
 * scrollability), and when its contents change — so a caller can, for example,
 * reveal an edge shadow only while there's hidden content in that direction.
 *
 * @example
 * const ref = useRef<HTMLUListElement>(null)
 * const { hasTopOverflow, hasBottomOverflow } = useTrackScrollOverflow(ref)
 * return <ul ref={ref} className={cx(hasBottomOverflow && "shadow-...")}>...</ul>
 */
export function useTrackScrollOverflow(ref: React.RefObject<HTMLElement>): {
  hasTopOverflow: boolean
  hasBottomOverflow: boolean
} {
  const [overflow, setOverflow] = useState({
    hasTopOverflow: false,
    hasBottomOverflow: false
  })

  useEffect(() => {
    const el = ref.current
    if (!el) return
    let frame = 0
    const measure = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const { scrollTop, scrollHeight, clientHeight } = el
        const isScrollable = scrollHeight > clientHeight
        // Round up so sub-pixel scroll heights still resolve to "at the bottom".
        const isAtBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight
        const hasTopOverflow = isScrollable && scrollTop > 0
        const hasBottomOverflow = isScrollable && !isAtBottom
        setOverflow(prev =>
          prev.hasTopOverflow === hasTopOverflow &&
          prev.hasBottomOverflow === hasBottomOverflow
            ? prev
            : { hasTopOverflow, hasBottomOverflow }
        )
      })
    }
    measure()

    el.addEventListener("scroll", measure, { passive: true })
    // ResizeObserver catches changes in scrollability; MutationObserver catches
    // items being added or removed.
    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(el)
    const mutationObserver = new MutationObserver(measure)
    mutationObserver.observe(el, { childList: true, subtree: true })

    return () => {
      cancelAnimationFrame(frame)
      el.removeEventListener("scroll", measure)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [ref])

  return overflow
}
